import { useState, useEffect } from "react";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { useHasActiveAiProvider } from "@/hooks/useHasActiveAiProvider";
import { AssistantDockedPanel } from "@/components/chat/AssistantDockedPanel";
import { AssistantMobile } from "@/components/chat/AssistantMobile";

/**
 * AI assistant panel — viewport-aware entry point (TES-1128, WP4).
 *
 * Capability gate (AC: "kein aktiver Provider → CapabilityWarning/Offline"):
 * renders nothing unless the `ai-assistant` feature flag is on AND a provider
 * is active (`useHasActiveAiProvider`). When the gate passes:
 *   - Desktop (lg+): docked right rail/panel (collapsible).
 *   - Mobile (< lg): floating pill/FAB + bottom sheet.
 *
 * Mounted once inside the AppLayout shell so it overlays every app page.
 */
export function AssistantPanel() {
  const { enabled, isLoading: flagLoading } = useFeatureFlag(
    "ai-assistant",
    FEATURE_FLAGS["ai-assistant"] ?? false,
  );
  const { data: hasProvider, isLoading: providerLoading } = useHasActiveAiProvider();
  const [desktopOpen, setDesktopOpen] = useState(true);
  // Mount only the panel for the current viewport (lg = 1024px, matching the panels'
  // CSS breakpoint) so the hidden one's useAgentChat/useAgentSuggestions don't run twice.
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  if (flagLoading || providerLoading || !enabled || !hasProvider) {
    return null;
  }

  return isDesktop ? (
    <AssistantDockedPanel open={desktopOpen} onOpenChange={setDesktopOpen} />
  ) : (
    <AssistantMobile />
  );
}
